import Bowser from 'bowser';
import axios from 'axios';
import { Modal } from 'bootstrap';
import { mount, componentOf } from '../dom-lifecycle';

mount('[data-receipt-file-upload]', function (el, refs, emit) {
    const scannerModal = Modal.getOrCreateInstance(refs.scannerModal);
    let ws = null;

    // Modal flow jest teleportowany z <template> do <body> przez komponent teleport, który
    // rejestruje się w dom-lifecycle po tym komponencie (index.js jest generowany alfabetycznie,
    // a initial sweep zachowuje kolejność rejestracji). W chwili montowania modal siedzi jeszcze
    // w <template>, a zawartość <template> jest inertna — document.querySelector jej nie widzi.
    // Dlatego referencję rozwiązujemy leniwie, przy pierwszym użyciu, i wtedy podpinamy mostki.
    let flowEl = null;
    let flow = null;

    function getFlow() {
        if (flowEl) return flow;

        flowEl = document.querySelector(`[data-receipt-flow-modal][data-id="${el.dataset.flowId}"]`);
        if (!flowEl) return null;

        flow = componentOf(flowEl);

        // The modal is teleported to <body>, so its events no longer bubble through the widget.
        // Bridge them: retry re-opens the picker, data is re-emitted from the in-form element.
        // „Dodaj zdjęcie ponownie" nie chowa modala flow po swojej stronie, a na mobile
        // klik w input otwiera modal skanera. Oba modale mają z-index 1055, a modal flow jest
        // teleportowany na koniec <body>, więc przy remisie wygrywa i zasłania skaner: user
        // widzi, że nic się nie stało, choć pod spodem startuje kamera, a FocusTrap
        // niewidocznego modala przechwytuje focus. Dlatego najpierw chowamy flow.
        // Chowamy jego publicznym hide() (nie flowModal.hide() Bootstrapa), bo tylko ono
        // ustawia scriptClose — inaczej zamknięcie poleciałoby jako `receipt:abort`.
        // Modal flow ma klasę `fade`, więc chowanie jest asynchroniczne: skaner otwieramy
        // dopiero po `hidden.bs.modal`, inaczej backdrop znikającego flow zdejmowałby
        // `modal-open` już po pokazaniu skanera (ten sam wyścig co przy odbiorze zdjęcia).
        flowEl.addEventListener('receipt:retry', () => {
            if (!flow || !flowEl.classList.contains('show')) {
                refs.input.click();
                return;
            }
            flowEl.addEventListener('hidden.bs.modal', () => refs.input.click(), { once: true });
            flow.hide();
        });
        flowEl.addEventListener('receipt:data', event => emit('receipt:data', event.detail));
        flowEl.addEventListener('receipt:abort', () => {
            refs.input.value = '';
            refs.input.dispatchEvent(new Event('change'));
        });

        return flow;
    }

    // Ten sam warunek decyduje o otwarciu skanera i o zwolnieniu kamery przy zamknięciu.
    // Wcześniej były to dwa różne kryteria (Bowser vs window.innerWidth < 992), więc na
    // wąskim oknie desktopu kamera nigdy nie startowała, a src i tak był zrywany.
    function isMobile() {
        return ['mobile', 'tablet'].includes(Bowser.parse(navigator.userAgent).platform.type);
    }

    refs.input.addEventListener('change', async () => {
        refs.receiptUuid.value = '';
        el.querySelectorAll('.file-error').forEach(error => error.remove());

        const file = refs.input.files[0];
        if (!file) return;

        getFlow()?.loading();
        const formData = new FormData();
        formData.set('file', file);
        formData.set('_token', refs.input.dataset.token);
        formData.set('wsUuid', await getWebsocketUuid());
        openWebSocket(formData.get('wsUuid'), refs.input.dataset.wsUrl);

        const response = await axios.post(refs.input.dataset.url, formData).catch(error => {
            const body = error.response?.data;
            const errors = body?.errors ?? body?.formErrors;
            const defaultMessage = 'Wystąpił nieznany błąd, proszę spróbować jeszcze raz.';
            // `error` to pojedynczy komunikat spoza walidacji formularza — m.in. blokada
            // wgrania dokładnie tego samego zdjęcia po odrzuconym odczycie.
            const message = errors ? Object.values(errors).flat().join('<br>') : (body?.error ?? defaultMessage);
            refs.input.closest('.form-group').insertAdjacentHTML('beforeend', `
                <div class="invalid-feedback d-block file-error">${message}</div>
            `);
            getFlow()?.hide();
        });
        closeWebSocket();
        if (!response) return;

        const data = response.data;

        // Odczyt się nie udał i user ma jeszcze prawo do poprawy zdjęcia — ekran błędu
        // z treścią zależną od tego, ile udało się odczytać, bez formularza ręcznego.
        if (!data.receiptUuid) {
            getFlow()?.error(data.scanResult);
            return;
        }

        refs.receiptUuid.value = data.receiptUuid;

        // Odczyt nieudany, ale próby wyczerpane (albo padło API) — user uzupełnia dane sam,
        // a zgłoszenie idzie na realną weryfikację ręczną zamiast się urwać.
        if (data.manualAllowed) {
            getFlow()?.manual({
                billData: data.billData ?? {},
                receiptUuid: data.receiptUuid,
                verifyUrl: refs.input.dataset.verifyUrl,
                notice: data.notice,
            });
            return;
        }

        getFlow()?.analyzed({
            billData: data.billData ?? {},
            receiptUuid: data.receiptUuid,
            acceptUrl: refs.input.dataset.acceptUrl,
            verifyUrl: refs.input.dataset.verifyUrl,
        });
        emit('receipt:data', { values: data.billData ?? {}, manual: false });
    });

    function getWebsocketUuid() {
        return fetch(refs.input.dataset.wsUuidUrl)
            .then(response => response.json())
            .then(data => data.uuid)
            .catch(() => {});
    }

    function openWebSocket(uuid, url) {
        closeWebSocket();
        if (refs.dataPreview) refs.dataPreview.innerHTML = '';
        ws = new WebSocket(url);
        ws.onmessage = data => appendPreview(data.data);
        ws.onopen = () => ws.send(JSON.stringify({ uuid }));
    }

    function closeWebSocket() {
        ws?.close();
        ws = null;
    }

    function appendPreview(text) {
        const current = getFlow();
        if (current) return current.appendPreview(text);
        if (!refs.dataPreview) return;
        refs.dataPreview.innerHTML += text.replace('\n', '<br />');
        refs.dataPreview.scrollTop = refs.dataPreview.scrollHeight;
    }

    refs.input.addEventListener('click', event => {
        if (!isMobile()) return;
        event.preventDefault();
        scannerModal.show();
    });

    refs.scannerModal.addEventListener('show.bs.modal', () => {
        refs.scannerFrame.src = refs.input.dataset.scannerUrl;
    });

    refs.scannerModal.addEventListener('hide.bs.modal', () => {
        if (isMobile()) refs.scannerFrame.removeAttribute('src');
    });

    window.addEventListener('message', event => {
        // Obrona w głąb: skaner jest serwowany z tej samej domeny (RECEIPT_SCANNER_URL to
        // ścieżka względna "/scanner/index.html", a CSP frame-src dopuszcza tylko 'self',
        // Google i Cloudflare), więc odrzucamy wszystko spoza naszego originu.
        if (event.origin !== window.location.origin) return;

        // Listener jest globalny, ale rejestrowany raz na instancję widgetu. Gdyby na stronie
        // był więcej niż jeden upload, obraz ze skanera trafiłby do wszystkich naraz —
        // przyjmujemy więc tylko wiadomości z iframe'a należącego do tej instancji.
        // Optional chaining, bo listener nigdy nie jest zdejmowany, a refs rozwiązuje
        // referencje leniwie: po usunięciu widgetu z DOM-u scannerFrame jest już null.
        if (event.source !== refs.scannerFrame?.contentWindow) return;

        let scannerData;
        try {
            scannerData = JSON.parse(event.data);
        } catch {
            return;
        }
        if (scannerData?.name != 'scannerImage') return;

        // scannerData.data to URL data: z base64 obrazu, przysłany przez iframe skanera.
        // CELOWO NIE używamy tu fetch(scannerData.data) — przeglądarka traktuje fetch() na
        // URL-u data: jako połączenie sieciowe podlegające dyrektywie CSP connect-src, a nasze
        // CSP (src/EventListener/CspListener.php) nie zawiera w niej schematu "data:". Efekt:
        // "Refused to connect" w konsoli i obraz nigdy nie trafiał do formularza (widoczne tylko
        // na telefonach, bo tam obraz pochodzi ze skanera, a nie z natywnego pickera plików).
        // Dekodowanie base64 lokalnie przez atob()/Uint8Array nie wykonuje żadnego I/O, więc
        // CSP w ogóle go nie dotyczy. Nie luzuj CSP zamiast tego — to naprawiałoby objaw,
        // a nie przyczynę.
        const match = /^data:([a-zA-Z0-9.+-]+\/[a-zA-Z0-9.+-]+)?(;charset=[^;,]+)?(;base64)?,([\s\S]*)$/
            .exec(scannerData.data ?? '');
        if (!match || !match[3]) {
            // Logujemy tylko krótki prefiks — pełne `data:` URL to megabajty base64,
            // a przy okazji zdjęcie paragonu użytkownika w konsoli.
            console.error(
                'Odebrano nieprawidłowy format obrazu ze skanera.',
                String(scannerData.data ?? '').slice(0, 64),
            );
            alert('Nie udało się odebrać zdjęcia ze skanera. Spróbuj ponownie.');
            scannerModal.hide();
            return;
        }

        // MIME bierzemy z nagłówka data: URL, a nie zakładamy go na sztywno — kamera koduje
        // przez toDataURL('image/jpeg', ...), ale przy wgraniu pliku z dysku skaner używa
        // FileReader.readAsDataURL, więc typ zależy od wgranego pliku.
        const mime = match[1] || 'image/jpeg';
        let bytes;
        try {
            const binary = atob(match[4]);
            bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
        } catch {
            // atob() rzuca na uszkodzonym/niepełnym base64 — user nie może zostać z zawieszonym
            // interfejsem, więc informujemy go i zamykamy skaner, żeby mógł spróbować jeszcze raz.
            console.error('Nie udało się zdekodować obrazu ze skanera (uszkodzone dane base64).');
            alert('Nie udało się odczytać zdjęcia ze skanera. Spróbuj ponownie.');
            scannerModal.hide();
            return;
        }

        // Rozszerzenie .png jest zastane i mylące — treść ze skanera to najczęściej JPEG
        // (kamera koduje przez toDataURL('image/jpeg', ...)). Nie zmieniamy tego tutaj.
        const file = new File([bytes], `receipt-${Date.now()}.png`, { type: mime });
        const container = new DataTransfer();
        container.items.add(file);
        refs.input.files = container.files;

        // Kolejność jest istotna. Modal skanera NIE ma klasy `fade`, więc jego hide() kończy
        // się synchronicznie: Bootstrap zdejmuje wtedy `modal-open` z <body> i resetuje
        // kompensację scrollbara. Gdyby wykonać go PO dispatchEvent('change'), synchroniczna
        // część handlera zdążyłaby już pokazać modal flow, a hide() skanera skasowałby świeżo
        // dodane `modal-open` — modal flow ma `fade`, więc jego _showElement przychodzi po
        // transition i klasy już nie przywraca. Efekt: strona pod otwartym flow przewija się
        // pod palcem. Chowamy więc skaner ZANIM flow zacznie się pokazywać.
        // Handler `hide.bs.modal` zdejmuje src z iframe'a (zwolnienie kamery) — obraz mamy już
        // w `file` i w refs.input.files, więc zerwanie iframe'a niczego nie psuje.
        scannerModal.hide();
        refs.input.dispatchEvent(new Event('change'));
    });
});
